// ═══════════════════════════════════════════════════════════════
// CAMPAIGNS VIEW — Direct Mail Campaign Tracking
// Path: src/views/CampaignsView.jsx
//
// Two modes:
//   1. LIST — all campaigns with stats
//   2. DETAIL — single campaign with recipient list + responses
//
// Recipients imported via CSV (SpektrixID or composite key match).
// Gifts linked to campaigns via gift entry dropdown.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Mail,
  Plus,
  Search,
  X,
  ChevronLeft,
  Edit3,
  Save,
  Trash2,
  Upload,
  UserPlus,
  Users,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Send,
  Award,
  Gift as GiftIcon,
  ChevronRight,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { ActionButton } from "../components/ui/ActionButton";
import { ConfirmDialog } from "../components/ui/Modal";
import { campaignService } from "../services/campaignService";
import { contactLogService } from "../services/contactLogService";
import { CampaignImportModal } from "../components/campaigns/CampaignImportModal";
import { ProspectImportModal } from "../components/campaigns/ProspectImportModal";
import { JOURNEY_STAGES, JOURNEY_STAGE_MAP } from "../constants/journeyStages";
import { HELP } from "../constants/helpContent";

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const STATUS_COLORS = {
  draft: "#6b7280",
  active: "#10b981",
  closed: "#f59e0b",
};

// ═══════════════════════════════════════════════════════════════
// MAIN VIEW
// ═══════════════════════════════════════════════════════════════

export const CampaignsView = () => {
  const { palette, goBack, goHome, navigate, members, showMessage, showLoading, hideLoading } = useApp();

  const [mode, setMode] = useState("list"); // 'list' | 'detail' | 'create'
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [showProspectImport, setShowProspectImport] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({ name: "", dateSent: "", segmentDescription: "" });
  const [stageFilter, setStageFilter] = useState("all");
  const [markingCards, setMarkingCards] = useState(false);

  // ── Load campaigns ──────────────────────────────────────────
  const loadCampaigns = useCallback(async () => {
    showLoading("Loading campaigns...");
    try {
      const data = await campaignService.getAll();
      setCampaigns(data);
    } catch (err) {
      // silent
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // ── Load detail ─────────────────────────────────────────────
  const loadDetail = useCallback(async (campaignId) => {
    showLoading("Loading campaign details...");
    try {
      const [campaign, recipientData, campaignStats] = await Promise.all([
        campaignService.getById(campaignId),
        campaignService.getRecipients(campaignId),
        campaignService.getCampaignStats(campaignId),
      ]);
      setSelectedCampaign(campaign);
      setRecipients(recipientData);
      setStats(campaignStats);
      setEditForm({
        name: campaign.name || "",
        dateSent: campaign.date_sent || "",
        segmentDescription: campaign.segment_description || "",
        status: campaign.status || "active",
      });
    } catch (err) {
      // silent
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  const handleOpenDetail = (campaign) => {
    setMode("detail");
    loadDetail(campaign.id);
  };

  // ── Create ──────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createForm.name.trim()) return;
    try {
      await campaignService.create(createForm);
      setCreateForm({ name: "", dateSent: "", segmentDescription: "" });
      setMode("list");
      loadCampaigns();
      showMessage("Campaign created");
    } catch (err) {
      showMessage("Error creating campaign", "error");
    }
  };

  // ── Save edits ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedCampaign) return;
    try {
      await campaignService.update(selectedCampaign.id, editForm);
      setEditing(false);
      loadDetail(selectedCampaign.id);
      showMessage("Campaign updated");
    } catch (err) {
      showMessage("Error updating campaign", "error");
    }
  };

  // ── Delete ──────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!selectedCampaign) return;
    try {
      await campaignService.remove(selectedCampaign.id);
      setShowDelete(false);
      setMode("list");
      loadCampaigns();
      showMessage("Campaign deleted", "warning");
    } catch (err) {
      showMessage("Error deleting campaign", "error");
    }
  };

  // ── Import complete ─────────────────────────────────────────
  const handleImportComplete = () => {
    setShowImport(false);
    if (selectedCampaign) loadDetail(selectedCampaign.id);
  };

  const handleProspectImportComplete = () => {
    setShowProspectImport(false);
    if (selectedCampaign) loadDetail(selectedCampaign.id);
  };

  // ── Journey Stage: Mark Card Sent (bulk) ────────────────────
  const handleMarkCardSent = async () => {
    const importedRecipients = recipients.filter(
      (rec) => (rec.journey_stage || "imported") === "imported"
    );
    if (importedRecipients.length === 0) return;

    setMarkingCards(true);
    try {
      const ids = importedRecipients.map((rec) => rec.id);
      await campaignService.bulkUpdateJourneyStage(ids, "card_sent");

      // Create contact log entries in chunks of 50
      const CHUNK = 50;
      for (let i = 0; i < importedRecipients.length; i += CHUNK) {
        const chunk = importedRecipients.slice(i, i + CHUNK);
        await Promise.all(
          chunk.map((rec) =>
            contactLogService.create(rec.member_id, {
              type: "card",
              subject: "Welcome Card Sent",
              notes: `Welcome card mailed — campaign: ${selectedCampaign?.name || "Unknown"}`,
              date: new Date().toISOString(),
            }).catch(() => {})
          )
        );
      }

      showMessage(`${importedRecipients.length} recipients marked as Card Sent`);
      loadDetail(selectedCampaign.id);
    } catch (err) {
      showMessage("Error updating journey stages", "error");
    } finally {
      setMarkingCards(false);
    }
  };

  // ── Journey Stage: Advance single recipient ─────────────────
  const handleAdvanceStage = async (recipient) => {
    const stageOrder = ["imported", "card_sent", "first_gift", "donor_of_record"];
    const currentIdx = stageOrder.indexOf(recipient.journey_stage || "imported");
    if (currentIdx >= stageOrder.length - 1) return; // already at end

    const nextStage = stageOrder[currentIdx + 1];
    try {
      await campaignService.updateJourneyStage(recipient.id, nextStage);
      showMessage(`Advanced to ${JOURNEY_STAGE_MAP[nextStage]?.name || nextStage}`);
      loadDetail(selectedCampaign.id);
    } catch (err) {
      showMessage("Error advancing stage", "error");
    }
  };

  // ── Recipient name helper ───────────────────────────────────
  const getRecipientName = (rec) => {
    if (!rec.members) return "—";
    return (
      rec.members.credentialed_full_name ||
      `${rec.members.first_name || ""} ${rec.members.last_name || ""}`.trim() ||
      "—"
    );
  };

  // ── Build gift response lookup for current campaign ─────────
  const [giftResponses, setGiftResponses] = useState({});
  useEffect(() => {
    if (!selectedCampaign) return;
    const loadGifts = async () => {
      try {
        const { data } = await (await import("../lib/supabase")).supabase
          .from("gifts")
          .select("member_id, amount, gift_date")
          .eq("campaign_id", selectedCampaign.id);

        const lookup = {};
        (data || []).forEach((g) => {
          if (!lookup[g.member_id]) lookup[g.member_id] = [];
          lookup[g.member_id].push(g);
        });
        setGiftResponses(lookup);
      } catch (err) {
        // silent
      }
    };
    loadGifts();
  }, [selectedCampaign, recipients]);

  // ── Search filter for recipients ────────────────────────────
  const filteredRecipients = useMemo(() => {
    let list = recipients;
    if (stageFilter !== "all") {
      list = list.filter((rec) => (rec.journey_stage || "imported") === stageFilter);
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((rec) => {
      const name = getRecipientName(rec).toLowerCase();
      const email = (rec.members?.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [recipients, search, stageFilter]);

  // ── Filtered campaigns ──────────────────────────────────────
  const filteredCampaigns = useMemo(() => {
    if (!search.trim()) return campaigns;
    const q = search.toLowerCase();
    return campaigns.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.segment_description || "").toLowerCase().includes(q)
    );
  }, [campaigns, search]);

  // ═════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Campaigns"
        subtitle="Direct mail tracking"
        icon={Mail}
        helpContent={HELP.campaigns}
        onBack={mode === "list" ? goBack : () => { setMode("list"); setSearch(""); }}
        onHome={goHome}
      />
      <ZoneTwoToolbar />

      {/* Zone 2 — Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 20px",
          background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
          flexWrap: "wrap",
        }}
      >
        {mode === "list" && (
          <>
            <ActionButton
              label="New Campaign"
              icon={Plus}
              small
              onClick={() => {
                setMode("create");
                setSearch("");
              }}
            />
            <div style={{ flex: 1 }} />
            <div style={{ position: "relative", minWidth: 200 }}>
              <Search
                size={ICON_SIZES.sm}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: palette.text3,
                  pointerEvents: "none",
                }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search campaigns..."
                style={{
                  width: "100%",
                  padding: "7px 10px 7px 32px",
                  borderRadius: 8,
                  fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY,
                  outline: "none",
                  background: palette.inputBg,
                  color: palette.inputColor,
                  border: `1px solid ${palette.inputBorder}`,
                }}
              />
            </div>
          </>
        )}
        {mode === "detail" && selectedCampaign && (
          <>
            <ActionButton
              label="Import Recipients"
              icon={Upload}
              small
              onClick={() => setShowImport(true)}
            />
            <ActionButton
              label="Import Prospects"
              icon={UserPlus}
              variant="secondary"
              small
              onClick={() => setShowProspectImport(true)}
            />
            <ActionButton
              label={markingCards ? "Marking..." : "Mark Card Sent"}
              icon={Send}
              variant="secondary"
              small
              onClick={handleMarkCardSent}
              disabled={markingCards || recipients.filter((r) => (r.journey_stage || "imported") === "imported").length === 0}
            />
            {editing ? (
              <>
                <ActionButton label="Cancel" variant="secondary" small onClick={() => setEditing(false)} />
                <ActionButton label="Save" icon={Save} small onClick={handleSave} />
              </>
            ) : (
              <>
                <ActionButton label="Edit" icon={Edit3} variant="secondary" small onClick={() => setEditing(true)} />
                <ActionButton label="Delete" icon={Trash2} variant="danger" small onClick={() => setShowDelete(true)} />
              </>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ position: "relative", minWidth: 200 }}>
              <Search
                size={ICON_SIZES.sm}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: palette.text3,
                  pointerEvents: "none",
                }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search recipients..."
                style={{
                  width: "100%",
                  padding: "7px 10px 7px 32px",
                  borderRadius: 8,
                  fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY,
                  outline: "none",
                  background: palette.inputBg,
                  color: palette.inputColor,
                  border: `1px solid ${palette.inputBorder}`,
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Stage filter pills (detail mode only) */}
      {mode === "detail" && selectedCampaign && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 20px",
            background: palette.cardBg,
            borderBottom: `1px solid ${palette.cardBorder}`,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: "0.55rem", fontWeight: 600, color: palette.text3, fontFamily: FONT_FAMILY, marginRight: 4 }}>
            Stage:
          </span>
          {[{ id: "all", name: "All", color: palette.text2 }, ...JOURNEY_STAGES].map((stage) => {
            const isActive = stageFilter === stage.id;
            const count = stage.id === "all"
              ? recipients.length
              : recipients.filter((r) => (r.journey_stage || "imported") === stage.id).length;
            return (
              <button
                key={stage.id}
                onClick={() => setStageFilter(stage.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "3px 10px",
                  borderRadius: 12,
                  fontSize: "0.55rem",
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  border: `1px solid ${isActive ? stage.color : palette.cardBorder}`,
                  background: isActive ? `${stage.color}15` : "transparent",
                  color: isActive ? stage.color : palette.text3,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {stage.name}
                <span style={{
                  fontSize: "0.5rem",
                  fontWeight: 700,
                  padding: "0 4px",
                  borderRadius: 6,
                  background: isActive ? `${stage.color}20` : `${palette.text3}10`,
                  color: isActive ? stage.color : palette.text3,
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* ── CREATE MODE ────────────────────────────────────── */}
        {mode === "create" && (
          <div
            style={{
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 10,
              padding: 24,
              maxWidth: 500,
            }}
          >
            <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY, marginBottom: 16 }}>
              New Campaign
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                  Campaign Name *
                </label>
                <input
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Spring Appeal 2026"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: FONT_SIZES.xs,
                    fontFamily: FONT_FAMILY,
                    outline: "none",
                    background: palette.inputBg,
                    color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`,
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                  Date Sent
                </label>
                <input
                  type="date"
                  value={createForm.dateSent}
                  onChange={(e) => setCreateForm((f) => ({ ...f, dateSent: e.target.value }))}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: FONT_SIZES.xs,
                    fontFamily: FONT_FAMILY,
                    outline: "none",
                    background: palette.inputBg,
                    color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`,
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                  Segment / Audience
                </label>
                <textarea
                  value={createForm.segmentDescription}
                  onChange={(e) => setCreateForm((f) => ({ ...f, segmentDescription: e.target.value }))}
                  placeholder="e.g., LYBNT donors $500+, Spektrix list"
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: FONT_SIZES.xs,
                    fontFamily: FONT_FAMILY,
                    outline: "none",
                    background: palette.inputBg,
                    color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`,
                    resize: "vertical",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <ActionButton label="Cancel" variant="secondary" small onClick={() => setMode("list")} />
                <ActionButton label="Create" icon={Plus} small onClick={handleCreate} disabled={!createForm.name.trim()} />
              </div>
            </div>
          </div>
        )}

        {/* ── LIST MODE ──────────────────────────────────────── */}
        {mode === "list" && (
          <>
            {filteredCampaigns.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 40,
                  color: palette.text3,
                  fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {campaigns.length === 0
                  ? "No campaigns yet. Click 'New Campaign' to get started."
                  : "No campaigns match your search."}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {filteredCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    palette={palette}
                    onClick={() => handleOpenDetail(campaign)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── DETAIL MODE ────────────────────────────────────── */}
        {mode === "detail" && selectedCampaign && (
          <>
            {/* Campaign Info */}
            <div
              style={{
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 10,
                padding: "16px 20px",
              }}
            >
              {editing ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <input
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    style={{
                      fontSize: FONT_SIZES.md,
                      fontWeight: 700,
                      fontFamily: FONT_FAMILY,
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: `1px solid ${palette.inputBorder}`,
                      background: palette.inputBg,
                      color: palette.inputColor,
                      outline: "none",
                    }}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <input
                      type="date"
                      value={editForm.dateSent}
                      onChange={(e) => setEditForm((f) => ({ ...f, dateSent: e.target.value }))}
                      style={{
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: `1px solid ${palette.inputBorder}`,
                        background: palette.inputBg,
                        color: palette.inputColor,
                        outline: "none",
                      }}
                    />
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                      style={{
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: `1px solid ${palette.inputBorder}`,
                        background: palette.inputBg,
                        color: palette.inputColor,
                        outline: "none",
                      }}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <textarea
                    value={editForm.segmentDescription}
                    onChange={(e) => setEditForm((f) => ({ ...f, segmentDescription: e.target.value }))}
                    rows={2}
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY,
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: `1px solid ${palette.inputBorder}`,
                      background: palette.inputBg,
                      color: palette.inputColor,
                      outline: "none",
                      resize: "vertical",
                    }}
                  />
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                      {selectedCampaign.name}
                    </span>
                    <span
                      style={{
                        fontSize: "0.55rem",
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 6,
                        background: `${STATUS_COLORS[selectedCampaign.status] || "#6b7280"}15`,
                        color: STATUS_COLORS[selectedCampaign.status] || "#6b7280",
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {selectedCampaign.status}
                    </span>
                  </div>
                  <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
                    {fmtDate(selectedCampaign.date_sent)}
                    {selectedCampaign.segment_description && ` · ${selectedCampaign.segment_description}`}
                  </div>
                </>
              )}
            </div>

            {/* Stats Bar */}
            {stats && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: 12,
                }}
              >
                <StatTile icon={Users} label="Recipients" value={stats.recipientCount} color="#3b82f6" palette={palette} />
                <StatTile icon={CheckCircle} label="Responded" value={stats.responseCount} color="#10b981" palette={palette} />
                <StatTile icon={DollarSign} label="Raised" value={fmtCurrency(stats.totalRaised)} color="#f59e0b" palette={palette} />
                <StatTile icon={TrendingUp} label="Rate" value={`${stats.responseRate}%`} color="#8b5cf6" palette={palette} />
                <StatTile icon={Award} label="Donor of Record" value={stats.stageCounts?.donor_of_record || 0} color="#10b981" palette={palette} />
              </div>
            )}

            {/* Journey Progress Bar */}
            {stats?.stageCounts && stats.recipientCount > 0 && (
              <JourneyProgressBar
                stageCounts={stats.stageCounts}
                total={stats.recipientCount}
                palette={palette}
                onStageClick={(stageId) => setStageFilter(stageId)}
                activeStage={stageFilter}
              />
            )}

            {/* Recipient List */}
            <div
              style={{
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: `1px solid ${palette.cardBorder}`,
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  color: palette.text1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Recipients ({filteredRecipients.length})
              </div>

              {filteredRecipients.length === 0 ? (
                <div
                  style={{
                    padding: 30,
                    textAlign: "center",
                    color: palette.text3,
                    fontSize: FONT_SIZES.xs,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {recipients.length === 0
                    ? "No recipients yet. Click 'Import Recipients' to upload a CSV."
                    : "No recipients match your search."}
                </div>
              ) : (
                <div style={{ maxHeight: 500, overflowY: "auto" }}>
                  {/* Table header */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 1fr 60px",
                      gap: 8,
                      padding: "8px 16px",
                      borderBottom: `1px solid ${palette.cardBorder}`,
                      fontSize: "0.6rem",
                      fontWeight: 600,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <span>Donor</span>
                    <span>Stage</span>
                    <span>Response</span>
                    <span></span>
                    <span></span>
                  </div>

                  {filteredRecipients.map((rec) => {
                    const gifts = giftResponses[rec.member_id] || [];
                    const totalResponse = gifts.reduce((s, g) => s + (Number(g.amount) || 0), 0);
                    const responded = gifts.length > 0;
                    const stageInfo = JOURNEY_STAGE_MAP[rec.journey_stage || "imported"] || JOURNEY_STAGE_MAP.imported;
                    const isLastStage = (rec.journey_stage || "imported") === "donor_of_record";

                    return (
                      <div
                        key={rec.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1fr 1fr 1fr 60px",
                          gap: 8,
                          padding: "8px 16px",
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          cursor: "pointer",
                          transition: "background 0.1s",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          const member = members.find((m) => m.id === rec.member_id);
                          if (member) navigate("memberDetail", member);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}06`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: FONT_SIZES.xs,
                              fontWeight: 600,
                              color: palette.text1,
                              fontFamily: FONT_FAMILY,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {getRecipientName(rec)}
                          </div>
                          {rec.members?.email && (
                            <div style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
                              {rec.members.email}
                            </div>
                          )}
                        </div>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.5rem",
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: `${stageInfo.color}15`,
                            color: stageInfo.color,
                            fontFamily: FONT_FAMILY,
                            width: "fit-content",
                          }}
                        >
                          {stageInfo.name}
                        </span>
                        <span
                          style={{
                            fontSize: FONT_SIZES.xs,
                            fontWeight: responded ? 600 : 400,
                            color: responded ? "#10b981" : palette.text3,
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          {responded ? fmtCurrency(totalResponse) : "No response"}
                        </span>
                        <div style={{ textAlign: "right" }}>
                          {responded && (
                            <CheckCircle size={ICON_SIZES.sm} style={{ color: "#10b981" }} />
                          )}
                        </div>
                        <div style={{ textAlign: "center" }}>
                          {!isLastStage && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAdvanceStage(rec);
                              }}
                              title={`Advance to next stage`}
                              style={{
                                background: "none",
                                border: `1px solid ${palette.cardBorder}`,
                                borderRadius: 6,
                                padding: "2px 4px",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                transition: "border-color 0.15s",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.borderColor = palette.accent1; }}
                              onMouseLeave={(e) => { e.currentTarget.style.borderColor = palette.cardBorder; }}
                            >
                              <ChevronRight size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ZoneThreeFooter />

      {/* Import Recipients Modal */}
      {showImport && selectedCampaign && (
        <CampaignImportModal
          campaignId={selectedCampaign.id}
          onClose={() => setShowImport(false)}
          onComplete={handleImportComplete}
        />
      )}

      {/* Import Prospects Modal */}
      {showProspectImport && selectedCampaign && (
        <ProspectImportModal
          campaignId={selectedCampaign.id}
          campaignName={selectedCampaign.name}
          onClose={() => setShowProspectImport(false)}
          onComplete={handleProspectImportComplete}
        />
      )}

      {/* Delete Confirm */}
      {showDelete && (
        <ConfirmDialog
          title="Delete Campaign"
          message={`Delete "${selectedCampaign?.name}" and all its recipients? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </ViewLayout>
  );
};

// ── Campaign Card (list mode) ─────────────────────────────────

const CampaignCard = ({ campaign, palette, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: palette.cardBg,
      border: `1px solid ${palette.cardBorder}`,
      borderRadius: 10,
      padding: "14px 18px",
      cursor: "pointer",
      transition: "background 0.1s",
      display: "flex",
      alignItems: "center",
      gap: 16,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}06`; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = palette.cardBg; }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: `${STATUS_COLORS[campaign.status] || "#6b7280"}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Mail size={ICON_SIZES.md} style={{ color: STATUS_COLORS[campaign.status] || "#6b7280" }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: FONT_SIZES.sm,
          fontWeight: 700,
          color: palette.text1,
          fontFamily: FONT_FAMILY,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {campaign.name}
      </div>
      <div style={{ fontSize: "0.6rem", color: palette.text3, fontFamily: FONT_FAMILY, marginTop: 2 }}>
        {fmtDate(campaign.date_sent)}
        {campaign.segment_description && ` · ${campaign.segment_description}`}
      </div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
          {campaign.recipient_count || 0}
        </div>
        <div style={{ fontSize: "0.5rem", color: palette.text3, fontFamily: FONT_FAMILY }}>recipients</div>
      </div>
      <span
        style={{
          fontSize: "0.55rem",
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 6,
          background: `${STATUS_COLORS[campaign.status] || "#6b7280"}15`,
          color: STATUS_COLORS[campaign.status] || "#6b7280",
          fontFamily: FONT_FAMILY,
        }}
      >
        {campaign.status}
      </span>
    </div>
  </div>
);

// ── Stat Tile ─────────────────────────────────────────────────

const StatTile = ({ icon: Icon, label, value, color, palette }) => (
  <div
    style={{
      background: palette.cardBg,
      border: `1px solid ${palette.cardBorder}`,
      borderRadius: 10,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 10,
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: `${color}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={ICON_SIZES.sm} style={{ color }} />
    </div>
    <div>
      <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
        {value}
      </div>
      <div style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>{label}</div>
    </div>
  </div>
);

// ── Journey Progress Bar ─────────────────────────────────────

const JourneyProgressBar = ({ stageCounts, total, palette, onStageClick, activeStage }) => {
  if (!total || total === 0) return null;

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        padding: "14px 18px",
      }}
    >
      <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, marginBottom: 10 }}>
        Welcome Journey Progress
      </div>
      {/* Segmented bar */}
      <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", gap: 2, marginBottom: 10 }}>
        {JOURNEY_STAGES.map((stage) => {
          const count = stageCounts[stage.id] || 0;
          const pct = (count / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={stage.id}
              onClick={() => onStageClick(stage.id)}
              title={`${stage.name}: ${count}`}
              style={{
                width: `${pct}%`,
                minWidth: count > 0 ? 4 : 0,
                background: stage.color,
                cursor: "pointer",
                opacity: activeStage === "all" || activeStage === stage.id ? 1 : 0.35,
                transition: "opacity 0.15s",
                borderRadius: 2,
              }}
            />
          );
        })}
      </div>
      {/* Legend */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {JOURNEY_STAGES.map((stage) => {
          const count = stageCounts[stage.id] || 0;
          return (
            <div
              key={stage.id}
              onClick={() => onStageClick(stage.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                cursor: "pointer",
                opacity: activeStage === "all" || activeStage === stage.id ? 1 : 0.5,
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: 2, background: stage.color, flexShrink: 0 }} />
              <span style={{ fontSize: "0.55rem", color: palette.text2, fontFamily: FONT_FAMILY }}>
                {stage.name}
              </span>
              <span style={{ fontSize: "0.55rem", fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
